import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import FancySwitch from '@/components/FancySwitch';
import { Input } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  ImagesBox,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import {
  ImageGrid,
  StyledTextArea,
  TextAreaBox,
  TextParametersBox,
  TextPlaceholder,
} from '../VariationTwo';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import Filler from '../shared/Filler';
import { ActionBox, ActionTitle } from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import NewUploadedImageRow from './NewUploadedImageRow';
import VariationsStyles from './VariationsStyles';

export interface ImageVariation {
  rightImageSrc: string;
  rightWatermarkTag: string;
  rightWatermarkPosition: string;
  rightText: string;
  rightTextPositon: string;
  rightQuotes: boolean;
  rightBreakes: boolean;
  divider: string;
  leftText: string;
  leftBrakes: boolean;
  reversed: boolean;
  leftFontSize?: number;
}

const StyledUploadMultipleImages = styled(UploadMultipleImages)`
  height: 358px;
`;

const StyledUploadedImages = styled(UploadedImages)`
  height: 382px;
`;

const StyledImagesBox = styled(ImagesBox)<{ $minusOrder: boolean }>`
  order: ${({ $minusOrder }) => ($minusOrder ? -1 : 1)};
`;

interface Props {
  inArticle?: boolean;
}

const VariationBoldTextImage: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [rightImages, setRightImages] = useState<ImageData[]>([]);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();
  const [leftBrakes, setLeftBrakes] = useState(false);
  const [leftText, setLeftText] = useState('');
  const [leftFontSize, setLeftFontSize] = useState('');
  const [isReversed, setIsReversed] = useState(false);

  const [showRightImagesModal, setShowRightImagesModal] = useState(false);

  const renderVariationStyles = () => {
    return <VariationsStyles />;
  };

  const generateVariations = () => {
    if (!rightImages.length) return;

    const newVariations = rightImages.map((rightImage) => ({
      rightImageSrc:
        rightImage.croppedImage ?? URL.createObjectURL(rightImage.image),
      rightWatermarkTag: getWatermarkTag(rightImage),
      rightWatermarkPosition: getWatermarkPosition(rightImage),
      rightText: rightImage.text,
      rightTextPositon: rightImage.textPosition,
      rightQuotes: rightImage.quotes,
      rightBreakes: rightImage.brakes,
      divider: rightImage.divider,
      leftText,
      leftBrakes,
      reversed: isReversed,
      leftFontSize: leftFontSize ? Number(leftFontSize) : undefined,
    }));

    setVariations(newVariations);
  };

  const renderRightImages = rightImages.map((image, index) => (
    <NewUploadedImageRow
      settings
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setRightImages}
      type="right"
    />
  ));

  const onReset = () => {
    setRightImages([]);
    setVariations([]);
    setImages([]);
    setLeftText('');
    setLeftFontSize('');
    setLeftBrakes(false);
    setIsReversed(false);
  };

  const isResetDisabled =
    !leftText &&
    !leftFontSize &&
    !leftBrakes &&
    !isReversed &&
    rightImages.length === 0;

  return (
    <>
      {renderVariationStyles()}
      <Box>
        <Header>
          <div>
            <Heading $type="button">Bold Text + Image Text</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach one image for the right side, add text and set needed
              parameters to get results
            </Text>
          </div>
          <HeaderButtons>
            <FancySwitch
              isActive={isReversed}
              onClick={() => setIsReversed((prev) => !prev)}
              text="Reversed"
            />
            <ResetButton
              color="red"
              onClick={onReset}
              disabled={isResetDisabled}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <ImageGrid>
            <TextParametersBox>
              <TextAreaBox>
                <div>
                  <LabelText $type="smallHeadline">
                    {isReversed ? 'Right' : 'Left'} Text
                  </LabelText>
                  <StyledTextArea
                    onChange={(e) => setLeftText(e.target.value)}
                    value={leftText}
                    placeholder="Add text"
                  ></StyledTextArea>
                </div>
                <div>
                  <LabelText $type="smallHeadline">Font Size</LabelText>
                  <Input
                    value={leftFontSize}
                    onChange={(e) => setLeftFontSize(e.target.value)}
                    placeholder="Font Size (px)"
                    type="number"
                    min="0"
                  />
                </div>
                <div>
                  <ActionTitle $type="smallHeadline">
                    Text Parameters
                  </ActionTitle>
                  <ActionBox>
                    <Checkbox
                      checked={leftBrakes}
                      onChange={() => setLeftBrakes((prev) => !prev)}
                    >
                      Custom brakes
                    </Checkbox>
                  </ActionBox>
                </div>
              </TextAreaBox>
              {!!rightImages?.length && <TextPlaceholder />}
            </TextParametersBox>
            <StyledImagesBox
              $minusOrder={isReversed}
              $hasImages={!!rightImages?.length}
              $hasLeftBorder={!isReversed}
            >
              <LabelText $type="smallHeadline">
                {isReversed ? 'Left' : 'Right'} Images
              </LabelText>
              {!!rightImages?.length ? (
                <>
                  <StyledUploadedImages>
                    {renderRightImages}
                  </StyledUploadedImages>
                  <MoreImages>
                    <AttachedCountBadge count={rightImages.length} />
                    <AddMoreButton
                      onClick={() => setShowRightImagesModal(true)}
                      color="colorized"
                    >
                      Add more images
                    </AddMoreButton>
                  </MoreImages>
                </>
              ) : (
                <StyledUploadMultipleImages
                  coverIndex={2}
                  noneDivider
                  setImages={setRightImages}
                />
              )}
            </StyledImagesBox>
          </ImageGrid>
        )}
      </Box>
      <Filler />
      <Filler fontFamily="Gothic" />
      {showContent && (
        <ButtonHolder>
          <Button disabled={!rightImages?.length} onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showRightImagesModal && (
          <MoreImagesModal
            onClose={() => setShowRightImagesModal(false)}
            setImages={setRightImages}
            coverIndex={2}
            blackDivider
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationBoldTextImage;
